import React from 'react';
import Collapsible from 'react-collapsible';
import ReactSVG from 'react-svg';
import {
  NeuroMessage,
  useNeuroMessages,
  useProduct,
  IgniteCommonActions,
  useGuideConfig,
  sendToLeadProcessor,
  useUser,
  useDispatch
} from '@ignitesales/neuro-application';
import { analyticEvents, processEvent } from '../utils/analytics';
import cognitoConfig from '../utils/cognitoConfig.js';
import saveKafkaStatus from '../../../../../regionsBranchTheme/src/shared/js/utils/saveKafkaStatus.js';


//TODO: fix the way we are using ContactFormModal, because it renders for every product. Need to only render once.
const Product = ({ product, isCollapsibleListOpen, onShowContactModal, appointmentScheduled }) => {
  const {
    descriptionAbbreviated,
    disclosures,
    features,
    name: productName,
    onProductActionClick,
    url
  } = useProduct(product);
  const { guideId } = useGuideConfig();
  const { uuid } = useUser();
  const dispatch = useDispatch();
  const leadProcessorCommand = { command: 'GenericRest', uuid, guideId };
  const messageContext = useNeuroMessages();
  const cssBEM = 'product';

  const actionUrl = (p, chunks) => (
    <a href={p.url} target="_blank" rel="noopener">{chunks}</a>
  )

  const productDetailsClicked = ( type, append = true ) => {
    onProductActionClick({
      actionName: IgniteCommonActions.PRODUCT_DETAILS_CLICKED,
      value: type,
      append
    });
  };

  const whyThisClicked = (action) => {
    processEvent({
      eventType: action === 'open' ? analyticEvents.accordionOpen : analyticEvents.accordionClose,
      description:  `Why this: ${productName}`
    });
    productDetailsClicked('features');
  };

  const learnMoreClicked = () => {
    processEvent({
      eventType: analyticEvents.click,
      description:  `Learn more: ${productName}`
    });
    productDetailsClicked('learnMore');
  };

  const sendLeadProcessor = async (action) => {
    try {
      const response = await sendToLeadProcessor(cognitoConfig(), leadProcessorCommand);
      const data = await response(dispatch)
      const result = data?.isSuccess ? data.isSuccess : data;
      saveKafkaStatus('kafka_lead_success','Product',`sendLeadProcessor-${action}`, result);
    } catch (error) {
      saveKafkaStatus('kafka_lead_error','Product',`sendLeadProcessor-${action}`, error.message);
    }
  }

  const ctaClicked = (type) => {
    processEvent({
      eventType: analyticEvents.click,
      description: `Call To Action: ${productName} - ${type}`
    });
    onProductActionClick({ actionName:'productApplyClicked' });
    if(guideId === 59) {
      sendLeadProcessor('ctaClicked');
    }
  };

  const renderProductActions = groupType => {
    return (
      <NeuroMessage id={`${product.id}.${groupType}`} shouldHideIfNotFound={true}>
        { (url) => <a
          href={ url }
          className={ `${ cssBEM }__url productActionBtn` }
          target="_blank"
          rel="noopener noreferrer"
          onClick={ () => ctaClicked(groupType) }
        ><NeuroMessage id={`product.${groupType}`} /></a>}
      </NeuroMessage>
    );
  };

  const renderProductActionsWithRules = () => {
    const count = parseInt(messageContext.getMessage(`${product.id}.url.count`) || "0");
    const urls = Array(count).fill(1).map((v, index) => {
      const msgIndex = index + 1;
      const id = `${product.id}.url.${msgIndex}`;
      return <NeuroMessage id={id} key={id}>
        { (url) => <a
          href={ url }
          className={ `${ cssBEM }__url productActionBtn` }
          target="_blank"
          rel="noopener noreferrer"
          onClick={ () => ctaClicked('applyNow') }
        ><NeuroMessage id={`${id}.ctaText`} /></a>}
      </NeuroMessage>;
    });

    return urls.length ? urls : renderProductActions('applyNow')
  };

  const renderAppointmentButton = groupType => {
    return (
      <NeuroMessage id={`${product.id}.${groupType}`} shouldHideIfNotFound={true}>
        { () =>
          <span className={ `${ cssBEM }__url productActionBtn` } onClick={ onShowContactModal } tabIndex="0">
            { appointmentScheduled ? <NeuroMessage id={'product.appointmentScheduled'} /> : <NeuroMessage id={`product.${groupType}`} /> }
          </span>
        }
      </NeuroMessage>
    );
  };

  const productHeader = <div className={ `${ cssBEM }__header` } tabIndex="0">
    <h2>
      { productName }
      <ReactSVG src="/classpath/assets/images/summaryIconChevronRight.svg"/>
    </h2>
  </div>;

  const urlValues = {
    'rewards': (...chunks) => actionUrl({url: 'https://www.regions.com/personal-banking/regions-rewards/regions-credit-card-rewards'}, chunks),
    'pricing': (...chunks) => actionUrl({url: 'https://www.regions.com/virtualdocuments/pricing_agreement.pdf'}, chunks),
  }

  const productDescription = () => <div className={ `${ cssBEM }__description` }>
    <p>{ descriptionAbbreviated }</p>
    <NeuroMessage id={`${product.id}.rewardsAndPricingInfo`} values={urlValues} shouldHideIfNotFound={true} />
    { url && <a href={ url } target="_blank" rel="noopener noreferrer" onClick={ learnMoreClicked }>
      <NeuroMessage id='learnMore' />
    </a>
    }
  </div>;

  const renderListItems = items => {
    return(
      items.map((item, index) => (
        <li key={`item-${index}`}>
          { item }
        </li>
      ))
    );
  };

  const collapsibleProps = {
    classParentString: cssBEM,
    openedClassName: `${ cssBEM } ${ cssBEM }--opened`,
    trigger: productHeader,
    open: isCollapsibleListOpen,
    onTriggerOpening: () => whyThisClicked('open'),
    onTriggerClosing: () => whyThisClicked('close'),
  };

  return <div className={ `${ cssBEM }__container ${ product.groupType }` }>
    <NeuroMessage id={ `product.${ product.groupType }`} tagName="h5"  />

    <Collapsible { ...collapsibleProps }
                 key={ product.id }
                 triggerSibling={productDescription}
    >
      <div className={ `${ cssBEM }__description` }>
        <NeuroMessage id="products.why" tagName="h3" />
        { features.length > 0 && <div className={ `${ cssBEM }__features` }><ul>{ renderListItems(features) } </ul></div> }
        {disclosures.length > 0 &&
        <div className={`${cssBEM}__disclosures`}>
          <NeuroMessage
            id="products.disclosures"
            tagName='div'
            className='title'/>
          <ol>{ renderListItems(disclosures) }</ol>
        </div>
        }
      </div>
    </Collapsible>

    <div className={ `${ cssBEM }__cta` }>
      {renderProductActionsWithRules()}
      {renderAppointmentButton('appointment')}
      {renderProductActions('wealthAdvisor')}
      {renderProductActions('financialAdvisor')}
    </div>
  </div>;
};

export default Product;
